import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GridRequest } from '../model/grid-request.model';
import { CollectionDto } from '../model/collection.model';
import { environment } from '../../environments/environment';
import { WimTreeNode } from '../model/wim-tree.node';
import {firstValueFrom, map, tap} from 'rxjs';
import { HttpClientHelper } from './http-client-helper.service';
import { ConsoleLoggerService } from './console-logger.service';
import { ProjectListItem } from '../model/project-list.item';
import { ProjectDetailItem } from '../model/project-detail.item';
import {ToolLocationListItem} from '../model/tool-location-list-item.model';
import {FilterDefinition} from '../model/filter-definition.model';
import {NodeType} from '../constants/node-type';
import {ToolDetailItem} from "../model/tool-detail.model";

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  constructor(private http: HttpClient, private httpClientHelper: HttpClientHelper,
              private log: ConsoleLoggerService) {
  }

  getProjects(request: GridRequest, selectedNode: WimTreeNode) {
    const params = this.httpClientHelper.createHttpParams();
    let url = this.createUrl(selectedNode);
    const observable = this.http.post<CollectionDto<ProjectListItem>>(url, request, { params: params })
      .pipe(tap(r => this.log.debug('Fetched projects: ', r)))
    return firstValueFrom(observable);
  }

  private createUrl(selectedNode: WimTreeNode) {
    return environment.apiUrl + this.getUrl(selectedNode);
  }

  private getUrl(selectedNode: WimTreeNode): string {
    const parent = (selectedNode.parent as WimTreeNode);
    const parentNodeType = parent?.nodeType;

    switch (parentNodeType) {
      case undefined:
        return `/projects/paged`;
      case NodeType.TOOL:
        return `/projects/tool/${parent.id}`;
      case NodeType.TOOL_LOCATION:
        return `/projects/location/${parent.id}`;
      default:
        throw new Error("getUrl(" + parentNodeType + ") not implemented");
    }

  }

  getProjectDetail(id: number) {
    const params = this.httpClientHelper.createHttpParams();
    let url = environment.apiUrl + `/projects/${id}`;
    const observable = this.http.get<ProjectDetailItem>(url, { params: params })
      .pipe(tap(r => this.log.debug('project: ', r)));
    return firstValueFrom(observable);
  }

  saveProject(detail: ProjectDetailItem) {
    const params = this.httpClientHelper.createHttpParams();
    let url = environment.apiUrl + `/projects`;
    let observable = this.http.post<number>(url, detail, { params: params })
      .pipe(tap(r => this.log.debug('saved: ', r)))

    return firstValueFrom(observable)
  }

  deleteProject(item: ProjectListItem | null) {
    if (item == null) {
      return;
    }

    const params = this.httpClientHelper.createHttpParams();
    let url = environment.apiUrl + `/projects/${item.id}`;
    let observable = this.http.delete<boolean>(url, { params: params })
      .pipe(tap(r => this.log.debug('project deleted ')))

    return firstValueFrom(observable)
  }

  searchProjectsByQueryAndTool(searchQuery: string, toolDetail: ToolDetailItem | null, mobile?: boolean | undefined) {

    const gridRequest: GridRequest = {
      pageRequest: {
        pageSize: 15,
        pageNumber: 0
      },
      filterDefinitions: this.getFilterDefinitions(mobile),
      searchQuery: searchQuery,
    };

    const params = this.httpClientHelper.createHttpParams();
    let url = `${environment.apiUrl}/projects/paged`;
    if (toolDetail) {
      url = `${environment.apiUrl}/projects/tool/${toolDetail.id}`;
    }

    const observable = this.http.post<CollectionDto<ProjectListItem>>(url, gridRequest, { params: params })
      .pipe(
        tap(r => this.log.debug('Projects: ', r)),
        map(response => response.items)
      );

    return firstValueFrom(observable);

  }

  searchProjectsByQuery(searchQuery: string, mobile?: boolean | undefined) {

    const gridRequest: GridRequest = {
      pageRequest: {
        pageSize: 15,
        pageNumber: 0
      },
      filterDefinitions: this.getFilterDefinitions(mobile),
      searchQuery: searchQuery,
    };

    const params = this.httpClientHelper.createHttpParams();
    let url = `${environment.apiUrl}/projects/paged`;
    const observable = this.http.post<CollectionDto<ProjectListItem>>(url, gridRequest, { params: params })
      .pipe(
        tap(r => this.log.debug('Projects: ', r)),
        map(response => response.items)
      );

    return firstValueFrom(observable);
  }

  private getFilterDefinitions(mobile: boolean | undefined) {
    if (!mobile) {
      return undefined;
    }

    const filterDefinition = {
      propertyName: 'mobile',
      value: String(mobile),
      filterOperator: '=='
    } as FilterDefinition;

    return [filterDefinition];
  }
}
